import React, { useEffect, useState, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import axios from "axios";
import MobileHeader from "./MobileHeader";
import MobileBottomNav from "./MobileBottomNav";
import MobileLogin from "./Mobilelogin";

// Looking one folder up in src/
import LocationModal from "../components/LocationModal"; 

import { API_URL } from "../api";
import { COLOR, FONT, GlobalMobileStyles, ThemeProvider } from "./mobiletheme";

const ACCENT = COLOR.primary;
const CATEGORY = "mobile_accessories"; 

const safeGet = (key, fallback = null) => {
  try { return localStorage.getItem(key) ?? fallback; } catch (_) { return fallback; }
};

function parsePrice(value) {
  if (value === null || value === undefined || value === "") return null;
  if (typeof value === "number") return Number.isFinite(value) ? value : null;
  const cleaned = String(value).replace(/[^0-9.]/g, "");
  if (cleaned === "") return null;
  const num = parseFloat(cleaned);
  return Number.isFinite(num) ? num : null;
}

function formatPrice(num) {
  return num % 1 === 0 ? num.toLocaleString("en-IN") : num.toLocaleString("en-IN", { minimumFractionDigits: 2, maximumFractionDigits: 2 });
}

function AccessoryCard({ product, cartItem, onAdd, onUpdateQty, addPending, actionPending }) {
  const [broken, setBroken] = useState(false);
  const { t, i18n } = useTranslation();

  const getName = (p) => {
    if (typeof p.name === "string") {
      try {
        const parsed = JSON.parse(p.name);
        return parsed[i18n.language] || parsed.en || Object.values(parsed)[0] || "";
      } catch {
        return p.name;
      }
    }
    return p.name?.[i18n.language] || p.name?.en || "";
  };

  const name = getName(product) || t("home.item", { defaultValue: "Item" });
  const image = product.image;
  const price = parsePrice(product.price);
  const mrp = parsePrice(product.mrp);
  const hasStrike = mrp !== null && price !== null && mrp > price;
  const discountPct = hasStrike ? Math.round(((mrp - price) / mrp) * 100) : 0;
  const unit = product.unit || "";

  const stockNum = Number(product.stock || 0);
  const outOfStock = stockNum <= 0;
  const lowStock = !outOfStock && stockNum <= 5;
  const atStockLimit = !!cartItem && stockNum > 0 && cartItem.quantity >= stockNum;

  return (
    <div style={outOfStock ? { ...styles.card, ...styles.cardOutOfStock } : styles.card}>
      <div style={styles.imgWrapper}>
        {!image || broken ? (
          <div style={styles.imgFallback}>{name[0]}</div>
        ) : (
          <img
            src={image}
            style={outOfStock ? { ...styles.image, ...styles.imageOutOfStock } : styles.image}
            alt={name}
            loading="lazy"
            onError={() => setBroken(true)}
          />
        )}
        {outOfStock && <div style={styles.outOfStockBadge}>{t("home.outOfStock", { defaultValue: "Out of Stock" })}</div>}
        {!outOfStock && lowStock && <div style={styles.lowStockBadge}>{stockNum === 1 ? t("home.oneLeft", { defaultValue: "1 left" }) : t("home.itemsLeft", { defaultValue: "{{count}} left", count: stockNum })}</div>}
      </div>

      {product.subcategory && <span style={styles.subcatTag}>{product.subcategory}</span>}
      <h4 style={styles.title}>{name}</h4>
      {unit && <span style={styles.unit}>{unit}</span>}
      {product.description && <p style={styles.description}>{product.description}</p>}
      {hasStrike && <span style={styles.discountTag}>{discountPct}% {t("home.off", { defaultValue: "OFF" })}</span>}

      <div style={styles.bottomRow}>
        <div style={styles.priceWrap}>
          <p style={styles.price}>{price !== null ? `₹${formatPrice(price)}` : t("home.na", { defaultValue: "N/A" })}</p>
          {hasStrike && <span style={styles.mrp}>₹{formatPrice(mrp)}</span>}
        </div>

        {outOfStock ? (
          <span style={styles.outOfStockText}>{t("home.outOfStock", { defaultValue: "Out of Stock" })}</span>
        ) : cartItem ? (
          <div style={styles.qtyBox}>
            <button style={styles.btn} disabled={actionPending || addPending} onClick={() => onUpdateQty(cartItem.id, "decrease")}>−</button>
            <span style={styles.qty}>{cartItem.quantity}</span>
            <button
              style={{ ...styles.btn, ...((atStockLimit || addPending) ? styles.btnDisabled : {}) }}
              disabled={actionPending || addPending || atStockLimit}
              onClick={() => onUpdateQty(cartItem.id, "increase")}
            >+</button>
          </div>
        ) : (
          <button style={styles.addBtn} disabled={addPending} onClick={() => onAdd(product)}>
            {addPending ? "..." : t("home.add", { defaultValue: "ADD" })}
          </button>
        )}
      </div>
    </div>
  );
}

function MobileAccessoriesContent() {
  const navigate = useNavigate();
  const { t } = useTranslation();
  const [headerHeight, setHeaderHeight] = useState(112);

  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(false);

  const [cartItems, setCartItems] = useState([]);
  const [pendingIds, setPendingIds] = useState({});
  const [showLogin, setShowLogin] = useState(false);
  const [activeSubcategory, setActiveSubcategory] = useState("All");

  const user_id = safeGet("phone", "guest");
  const isLoggedIn = () => safeGet("isLoggedIn") === "true";

  const loadItems = useCallback(async () => {
    setLoading(true);
    setError(false);
    try {
      const res = await axios.get(`${API_URL}/items/mobile-accessories`);
      if (res.data.success) setItems(res.data.items || []);
      else setError(true);
    } catch (err) {
      console.log("Load mobile accessories error:", err);
      setError(true);
    } finally {
      setLoading(false);
    }
  }, []);

  const loadCart = useCallback(async () => {
    if (!isLoggedIn() || user_id === "guest") {
      setCartItems([]);
      return;
    }
    try {
      const res = await axios.get(`${API_URL}/cart/${user_id}`);
      if (res.data.success) setCartItems(res.data.cart || []);
    } catch (err) {
      console.log("Load cart error:", err);
    }
  }, [user_id]);

  useEffect(() => {
    loadItems();
    loadCart();
  }, [loadItems, loadCart]);

  const notifyCartUpdate = () => {
    window.dispatchEvent(new Event("cartUpdated"));
  };

  const addToCart = async (product) => {
    if (!isLoggedIn() || user_id === "guest") {
      setShowLogin(true);
      return;
    }
    const pendingKey = `add-${product.id}`;
    if (pendingIds[pendingKey]) return;
    setPendingIds((p) => ({ ...p, [pendingKey]: true }));

    const tempId = `temp-${product.id}`;
    setCartItems((prev) => [...prev, { id: tempId, product_id: product.id, category: CATEGORY, quantity: 1 }]);
    notifyCartUpdate();

    try {
      const res = await axios.post(`${API_URL}/cart/add`, { user_id, product_id: product.id, category: CATEGORY });
      const real = res.data?.cartItem;
      setCartItems((prev) => prev.map((i) => (i.id === tempId
        ? { ...i, id: real ? real.id : i.id, quantity: real ? real.quantity : i.quantity }
        : i)));
    } catch (err) {
      setCartItems((prev) => prev.filter((i) => i.id !== tempId));
      alert(t("home.addError", { defaultValue: "Couldn’t add item — please try again." }));
    } finally {
      notifyCartUpdate();
      setPendingIds((p) => { const next = { ...p }; delete next[pendingKey]; return next; });
    }
  };

  const updateQty = async (cart_id, action) => {
    if (typeof cart_id === "string" && cart_id.startsWith("temp-")) return;
    if (pendingIds[cart_id]) return;
    const item = cartItems.find((i) => i.id === cart_id);
    if (!item) return;
    const prevSnapshot = cartItems;
    setPendingIds((p) => ({ ...p, [cart_id]: true }));

    if (action === "decrease" && item.quantity <= 1) {
      setCartItems((prev) => prev.filter((i) => i.id !== cart_id));
    } else {
      setCartItems((prev) => prev.map((i) => i.id === cart_id
        ? { ...i, quantity: action === "increase" ? i.quantity + 1 : i.quantity - 1 }
        : i));
    }
    notifyCartUpdate();

    try {
      if (action === "decrease" && item.quantity <= 1) {
        await axios.delete(`${API_URL}/cart/${cart_id}`);
      } else {
        await axios.put(`${API_URL}/cart/${action}/${cart_id}`);
      }
    } catch (err) {
      setCartItems(prevSnapshot);
    } finally {
      notifyCartUpdate();
      setPendingIds((p) => { const next = { ...p }; delete next[cart_id]; return next; });
    }
  };

  const subcategories = ["All", ...Array.from(new Set(items.map((i) => i.subcategory).filter(Boolean)))];
  const visibleItems = activeSubcategory === "All" ? items : items.filter((i) => i.subcategory === activeSubcategory);

  return (
    <ThemeProvider>
    <div style={styles.container}>
      <GlobalMobileStyles />
      <MobileHeader
        showLogo={false}
        showTitleBar={true}
        showBackButton={true}
        onBack={() => navigate(-1)}
        title={t("categories.accessories", "Mobile Accessories")}
        onHeightChange={setHeaderHeight}
      />

      <div style={{ ...styles.content, paddingTop: `${headerHeight + 16}px` }}>
        {subcategories.length > 2 && (
          <div style={styles.chipRow}>
            {subcategories.map((sub) => (
              <button
                key={sub}
                className="sb-tap"
                onClick={() => setActiveSubcategory(sub)}
                style={activeSubcategory === sub ? styles.chipActive : styles.chip}
              >
                {sub}
              </button>
            ))}
          </div>
        )}

        {loading ? (
          <div style={styles.grid}>
            {Array.from({ length: 6 }).map((_, i) => (
              <div key={i} className="mil-skeleton" style={styles.skeletonCard} />
            ))}
          </div>
        ) : error ? (
          <div style={styles.centerPrompt}>
            <p>{t("mobile.couldntLoadResults", { defaultValue: "Couldn't load products." })}</p>
            <button style={styles.retryBtn} onClick={loadItems}>{t("mobile.retry", { defaultValue: "Retry" })}</button>
          </div>
        ) : visibleItems.length === 0 ? (
          <div style={styles.centerPrompt}>
            <p>{t("home.noAccessories", { defaultValue: "No accessories available right now." })}</p>
          </div>
        ) : (
          <div style={styles.grid}>
            {visibleItems.map((item) => {
              const cartItem = cartItems.find((c) => c.product_id === item.id && c.category === CATEGORY);
              const addPending = !!pendingIds[`add-${item.id}`];
              const actionPending = cartItem ? !!pendingIds[cartItem.id] : false;
              return (
                <AccessoryCard
                  key={item.id}
                  product={item}
                  cartItem={cartItem}
                  onAdd={addToCart}
                  onUpdateQty={updateQty}
                  addPending={addPending}
                  actionPending={actionPending}
                />
              );
            })}
          </div>
        )}
      </div>

      {showLogin && <MobileLogin onClose={() => setShowLogin(false)} onLoginSuccess={() => { setShowLogin(false); loadCart(); }} />}

      <MobileBottomNav hideLang />
    </div>
    </ThemeProvider>
  );
}

// ==========================================
// STYLES
// ==========================================
const styles = {
  container: {
    backgroundColor: COLOR.mist,
    minHeight: "100vh",
    display: "flex",
    flexDirection: "column",
    fontFamily: FONT.body,
  },
  content: {
    flex: 1,
    padding: "0 12px 24px",
    boxSizing: "border-box",
  },
  chipRow: {
    display: "flex",
    gap: "8px",
    overflowX: "auto",
    padding: "4px 2px 14px",
  },
  chip: {
    flexShrink: 0,
    padding: "8px 14px",
    borderRadius: "999px",
    border: `1px solid ${COLOR.line}`,
    background: COLOR.paper,
    color: COLOR.ink,
    fontSize: "12.5px",
    fontWeight: 600,
    cursor: "pointer",
  },
  chipActive: {
    flexShrink: 0,
    padding: "8px 14px",
    borderRadius: "999px",
    border: `1px solid ${COLOR.primaryDark}`,
    background: COLOR.primaryDark,
    color: COLOR.onPrimary,
    fontSize: "12.5px",
    fontWeight: 700,
    cursor: "pointer",
  },
  grid: {
    display: "grid",
    gridTemplateColumns: "1fr 1fr",
    gap: "14px",
  },
  skeletonCard: {
    borderRadius: "16px",
    border: `1px solid ${COLOR.line}`,
    backgroundColor: COLOR.paper,
    aspectRatio: "0.72",
  },
  card: {
    backgroundColor: COLOR.paper,
    borderRadius: "16px",
    padding: "12px",
    display: "flex",
    flexDirection: "column",
    boxShadow: "0 2px 12px rgba(0,0,0,0.06)",
  },
  cardOutOfStock: { opacity: 0.6 },
  imgWrapper: { width: "100%", aspectRatio: "1", borderRadius: "12px", overflow: "hidden", marginBottom: "8px", backgroundColor: COLOR.mist, position: "relative" },
  image: { width: "100%", height: "100%", objectFit: "cover" },
  imageOutOfStock: { filter: "grayscale(1)" },
  imgFallback: { width: "100%", height: "100%", display: "flex", alignItems: "center", justifyContent: "center", fontSize: "28px", backgroundColor: COLOR.mist, color: COLOR.muted, fontFamily: FONT.display, fontWeight: 800 },
  outOfStockBadge: { position: "absolute", top: "6px", left: "6px", backgroundColor: COLOR.ink, color: COLOR.onPrimary, fontSize: "9px", fontWeight: "bold", padding: "3px 6px", borderRadius: "5px", textTransform: "uppercase" },
  lowStockBadge: { position: "absolute", top: "6px", left: "6px", backgroundColor: COLOR.pop, color: COLOR.onPrimary, fontSize: "9px", fontWeight: "bold", padding: "3px 6px", borderRadius: "5px" },
  subcatTag: { fontSize: "10px", fontWeight: 700, color: COLOR.primaryDark, marginBottom: "2px" },
  title: { fontSize: "13.5px", fontWeight: 600, color: COLOR.ink, margin: "0 0 2px 0", lineHeight: 1.3 },
  unit: { fontSize: "12px", color: COLOR.muted, marginBottom: "4px", display: "block" },
  description: { fontSize: "11.5px", color: COLOR.muted, margin: "0 0 8px 0", lineHeight: 1.4, display: "-webkit-box", WebkitLineClamp: 2, WebkitBoxOrient: "vertical", overflow: "hidden" },
  discountTag: { alignSelf: "flex-start", backgroundColor: COLOR.errorSoft, color: COLOR.error, fontSize: "9px", fontWeight: 700, padding: "2px 6px", borderRadius: "6px", marginBottom: "6px" },
  bottomRow: { display: "flex", justifyContent: "space-between", alignItems: "center", marginTop: "auto", gap: "6px" },
  priceWrap: { display: "flex", flexDirection: "column" },
  price: { fontWeight: 900, fontSize: "14px", color: COLOR.ink, margin: 0 },
  mrp: { fontSize: "10.5px", color: COLOR.muted, textDecoration: "line-through", fontWeight: 500 },
  outOfStockText: { fontSize: "11px", fontWeight: "bold", color: COLOR.error },
  addBtn: { padding: "6px 14px", backgroundColor: ACCENT, color: COLOR.onPrimary, border: "none", borderRadius: "10px", fontWeight: 700, fontSize: "13.5px", cursor: "pointer" },
  qtyBox: { display: "flex", alignItems: "center", gap: "8px", backgroundColor: ACCENT, borderRadius: "10px", padding: "4px 10px" },
  btn: { border: "none", background: "none", color: COLOR.onPrimary, fontWeight: "bold", fontSize: "18px", cursor: "pointer", padding: 0 },
  btnDisabled: { opacity: 0.4, cursor: "not-allowed" },
  qty: { fontSize: "15px", fontWeight: 700, color: COLOR.onPrimary, minWidth: "14px", textAlign: "center" },
  centerPrompt: { display: "flex", flexDirection: "column", alignItems: "center", justifyContent: "center", textAlign: "center", padding: "60px 20px", gap: "12px" },
  retryBtn: { backgroundColor: COLOR.primaryDark, color: COLOR.onPrimary, border: "none", padding: "10px 20px", borderRadius: "10px", fontWeight: 700, cursor: "pointer" },
};

// ==========================================
// GATE ICONS
// ==========================================
function BackArrowIcon() {
  return (
    <svg width="18" height="18" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
      <path d="M15 5l-7 7 7 7" stroke={COLOR.ink} strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
    </svg>
  );
}

function LocationPinBigIcon() {
  return (
    <svg width="34" height="34" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
      <path d="M12 22s7-6.2 7-12a7 7 0 1 0-14 0c0 5.8 7 12 7 12z" stroke={COLOR.primaryDark} strokeWidth="1.8" strokeLinejoin="round" />
      <circle cx="12" cy="10" r="2.6" stroke={COLOR.primaryDark} strokeWidth="1.8" />
    </svg>
  );
}

function CrosshairSmallIcon() {
  return (
    <svg width="16" height="16" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
      <circle cx="12" cy="12" r="3" stroke={COLOR.onPrimary} strokeWidth="2" />
      <path d="M12 2v3M12 19v3M2 12h3M19 12h3" stroke={COLOR.onPrimary} strokeWidth="2" strokeLinecap="round" />
    </svg>
  );
}

function SearchSmallIcon() {
  return (
    <svg width="15" height="15" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
      <circle cx="10.5" cy="10.5" r="6.5" stroke={COLOR.muted} strokeWidth="2" />
      <line x1="19.5" y1="19.5" x2="15.2" y2="15.2" stroke={COLOR.muted} strokeWidth="2" strokeLinecap="round" />
    </svg>
  );
}

function MapPinDotIcon() {
  return (
    <svg width="14" height="14" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
      <path d="M12 21s6-5.4 6-10.2A6 6 0 0 0 6 10.8C6 15.6 12 21 12 21z" stroke={COLOR.primaryDark} strokeWidth="1.8" strokeLinejoin="round" />
    </svg>
  );
}

// ==========================================
// LOCATION GATE WRAPPER
// ==========================================
// Blinkit-style flow: the moment this page opens with no saved delivery
// address, the location sheet pops up right away so the person picks a
// spot (search or "use current location") before anything else loads.
// Once a location is chosen we re-check it against the serviceable-pincode
// list — a match opens the catalog immediately, a miss shows a clean
// "not deliverable here" screen with the full list of areas we do cover.
export default function MobileAccessories() {
  const { t } = useTranslation();
  const navigate = useNavigate();

  // sessionStorage (not localStorage) so this naturally resets when the
  // browser tab/window is closed, but persists across in-app navigation
  // (home -> category -> home -> category) for the rest of the visit.
  const SESSION_FLAG = "mobileAccessLocationEntered";
  const [locationEnteredThisSession, setLocationEnteredThisSession] = useState(
    () => sessionStorage.getItem(SESSION_FLAG) === "true"
  );

  const [showLocationModal, setShowLocationModal] = useState(false);
  const [isServiceable, setIsServiceable] = useState(false);
  const [unservedAddress, setUnservedAddress] = useState("");
  const [hasChecked, setHasChecked] = useState(false);

  const [deliverableAreas, setDeliverableAreas] = useState([]);
  const [areasLoading, setAreasLoading] = useState(true);
  const [areasError, setAreasError] = useState(false);
  const [areaSearch, setAreaSearch] = useState("");

  // Strict location verification bound to the global address string
  const checkGlobalLocation = useCallback((areas) => {
    // ONLY read the global location set by the website
    const globalAddress = localStorage.getItem("address") || "";

    if (!globalAddress) {
      setIsServiceable(false);
      setUnservedAddress("");
      setHasChecked(true);
      return;
    }

    // Try to extract a 6 digit pin from the global address string
    let pin = null;
    const match = globalAddress.match(/\b\d{6}\b/);
    if (match) pin = match[0];

    // Determine if it matches our serviceable table
    const isMatch = areas.some((d) => {
      // 1. If we found a Pin, check exactly against that Pin
      if (pin) return String(d.pincode) === String(pin);
      // 2. If NO Pin was found (e.g. GPS just gave the area name), text match
      if (d.area_name) return globalAddress.toLowerCase().includes(d.area_name.toLowerCase());
      return false;
    });

    setIsServiceable(isMatch);
    setUnservedAddress(isMatch ? "" : globalAddress);
    setHasChecked(true);
  }, []);

  useEffect(() => {
    // Force the location sheet only the first time this category is opened
    // in the current browsing session (sessionStorage clears itself when the
    // tab/browser closes). Once a location has been entered, navigating
    // home and back into this category won't re-prompt — it just re-checks
    // the already-entered address in the background.
    if (!locationEnteredThisSession) {
      setShowLocationModal(true);
    }

    setAreasLoading(true);
    setAreasError(false);
    axios
      .get(`${API_URL}/items/serviceable-pincodes`)
      .then((res) => {
        if (res.data.success) {
          const areas = res.data.pincodes || [];
          setDeliverableAreas(areas);
          checkGlobalLocation(areas);
        } else {
          setAreasError(true);
        }
      })
      .catch((err) => {
        console.error("Failed to load pincodes", err);
        setAreasError(true);
      })
      .finally(() => setAreasLoading(false));

    // If the user changes location in the top header while this page is open,
    // this event listener catches it and re-runs the strict check instantly.
    const handleGlobalLocationChange = () => {
      setDeliverableAreas((currentAreas) => {
        if (currentAreas.length > 0) checkGlobalLocation(currentAreas);
        return currentAreas;
      });
    };

    window.addEventListener("locationUpdated", handleGlobalLocationChange);
    return () => window.removeEventListener("locationUpdated", handleGlobalLocationChange);
  }, [checkGlobalLocation]);

  const handleLocationSelect = (address, addrObj) => {
    // When a user selects a location inside THIS page's modal,
    // we sync it to the global website storage so the whole app knows.
    const finalAddress = addrObj?.full_address || address;
    localStorage.setItem("address", finalAddress);

    // Dispatch the global event so MobileHeader (and this component) updates
    window.dispatchEvent(new Event("locationUpdated"));

    // Mark that a location has been entered for the rest of this browsing
    // session, so returning to this category later (without closing the
    // tab/browser) won't force the sheet open again.
    sessionStorage.setItem(SESSION_FLAG, "true");
    setLocationEnteredThisSession(true);

    // Run the check immediately
    checkGlobalLocation(deliverableAreas);
  };

  // Only drop into the catalog once the location sheet is closed — on the
  // very first visit this session that means the person has actually made
  // a selection (the sheet is non-dismissible until then); on later visits
  // this session the sheet never opens at all, so this resolves instantly.
  if (isServiceable && !showLocationModal) {
    return <MobileAccessoriesContent />;
  }

  const hasSavedAddress = !!localStorage.getItem("address");
  const filteredAreas = deliverableAreas.filter((a) =>
    (a.area_name || "").toLowerCase().includes(areaSearch.trim().toLowerCase())
  );

  return (
    <div style={gateStyles.page}>
      <GlobalMobileStyles />

      <div style={gateStyles.topBar}>
        <button onClick={() => navigate(-1)} style={gateStyles.backBtn} aria-label={t("common.back", { defaultValue: "Back" })}>
          <BackArrowIcon />
        </button>
        <span style={gateStyles.topBarTitle}>{t("categories.accessories", { defaultValue: "Mobile Accessories" })}</span>
        <span style={gateStyles.topBarSpacer} />
      </div>

      <div style={gateStyles.hero}>
        <div style={gateStyles.heroIconWrap}>
          <LocationPinBigIcon />
        </div>

        {!hasChecked ? (
          <>
            <h2 style={gateStyles.heroTitle}>{t("mobile.checkingLocation", { defaultValue: "Checking your location…" })}</h2>
            <p style={gateStyles.heroSubtitle}>{t("mobile.holdOn", { defaultValue: "One second while we see what we can deliver to you." })}</p>
          </>
        ) : unservedAddress ? (
          <>
            <h2 style={gateStyles.heroTitle}>{t("mobile.notDeliverableTitle", { defaultValue: "We don't deliver here yet" })}</h2>
            <p style={gateStyles.heroSubtitle}>
              {t("mobile.notDeliverableBody", { defaultValue: "We currently can't deliver mobile accessories to" })}{" "}
              <span style={gateStyles.heroSubtitleStrong}>{unservedAddress}</span>
            </p>
          </>
        ) : (
          <>
            <h2 style={gateStyles.heroTitle}>{t("mobile.setLocationTitle", { defaultValue: "Set your delivery location" })}</h2>
            <p style={gateStyles.heroSubtitle}>{t("mobile.setLocationBody", { defaultValue: "Tell us where to deliver so we can show you what's available nearby." })}</p>
          </>
        )}

        <button onClick={() => setShowLocationModal(true)} style={gateStyles.locationBtn} className="sb-tap">
          <CrosshairSmallIcon />
          {hasSavedAddress
            ? t("mobile.changeLocation", { defaultValue: "Change Location" })
            : t("mobile.setLocation", { defaultValue: "Set Delivery Location" })}
        </button>
      </div>

      <div style={gateStyles.areasCard}>
        <div style={gateStyles.areasHeader}>
          <h3 style={gateStyles.areasTitle}>{t("mobile.areasWeDeliverTo", { defaultValue: "Areas we deliver to" })}</h3>
          {!areasLoading && !areasError && deliverableAreas.length > 0 && (
            <span style={gateStyles.areasCount}>{deliverableAreas.length}</span>
          )}
        </div>

        {!areasLoading && !areasError && deliverableAreas.length > 6 && (
          <div style={gateStyles.areaSearchWrap}>
            <SearchSmallIcon />
            <input
              value={areaSearch}
              onChange={(e) => setAreaSearch(e.target.value)}
              placeholder={t("mobile.searchAreas", { defaultValue: "Search your area…" })}
              style={gateStyles.areaSearchInput}
            />
          </div>
        )}

        {areasLoading ? (
          <div style={gateStyles.areasList}>
            {Array.from({ length: 4 }).map((_, i) => (
              <div key={i} className="mil-skeleton" style={gateStyles.areaSkeleton} />
            ))}
          </div>
        ) : areasError ? (
          <p style={gateStyles.areasEmptyText}>
            {t("mobile.areasLoadError", { defaultValue: "Couldn't load delivery areas. Please check back shortly." })}
          </p>
        ) : deliverableAreas.length === 0 ? (
          <p style={gateStyles.areasEmptyText}>{t("mobile.noAreasYet", { defaultValue: "No delivery areas set up yet." })}</p>
        ) : filteredAreas.length === 0 ? (
          <p style={gateStyles.areasEmptyText}>{t("mobile.noAreasMatch", { defaultValue: "No matching areas found." })}</p>
        ) : (
          <div style={gateStyles.areasList}>
            {filteredAreas.map((area) => (
              <div key={area.pincode} style={gateStyles.areaChip}>
                <span style={gateStyles.areaChipIconWrap}><MapPinDotIcon /></span>
                <span style={gateStyles.areaChipName}>{area.area_name}</span>
                <span style={gateStyles.areaChipPin}>{area.pincode}</span>
              </div>
            ))}
          </div>
        )}
      </div>

      {showLocationModal && (
        <LocationModal
          onClose={() => {
            setShowLocationModal(false);
            setHasChecked(true);
          }}
          onSelect={handleLocationSelect}
          // Non-dismissible only for the mandatory first-visit-this-session
          // prompt — clicking outside or the X can't skip it without a
          // location being picked. Once a location's been entered, reopening
          // via "Change Location" is a normal, closable sheet.
          dismissible={locationEnteredThisSession}
        />
      )}
    </div>
  );
}

// ==========================================
// GATE STYLES — matches the app's light theme
// (COLOR / FONT from mobiletheme) instead of a
// standalone dark debug-style screen.
// ==========================================
const gateStyles = {
  page: {
    backgroundColor: COLOR.mist,
    minHeight: "100vh",
    display: "flex",
    flexDirection: "column",
    fontFamily: FONT.body,
  },
  topBar: {
    display: "flex",
    alignItems: "center",
    justifyContent: "space-between",
    padding: "14px 16px",
    backgroundColor: COLOR.paper,
    borderBottom: `1px solid ${COLOR.line}`,
    position: "sticky",
    top: 0,
    zIndex: 5,
  },
  backBtn: {
    width: "34px",
    height: "34px",
    borderRadius: "999px",
    border: "none",
    background: COLOR.mist,
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    cursor: "pointer",
    flexShrink: 0,
  },
  topBarTitle: { fontSize: "15px", fontWeight: 700, color: COLOR.ink },
  topBarSpacer: { width: "34px" },

  hero: {
    display: "flex",
    flexDirection: "column",
    alignItems: "center",
    textAlign: "center",
    padding: "34px 24px 26px",
  },
  heroIconWrap: {
    width: "72px",
    height: "72px",
    borderRadius: "999px",
    background: COLOR.mist,
    border: `1px solid ${COLOR.line}`,
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    marginBottom: "16px",
  },
  heroTitle: {
    fontSize: "17.5px",
    fontWeight: 800,
    color: COLOR.ink,
    margin: "0 0 8px 0",
    fontFamily: FONT.display,
  },
  heroSubtitle: {
    fontSize: "13.5px",
    color: COLOR.muted,
    margin: "0 0 22px 0",
    lineHeight: 1.5,
    maxWidth: "320px",
  },
  heroSubtitleStrong: { color: COLOR.ink, fontWeight: 700 },

  locationBtn: {
    display: "inline-flex",
    alignItems: "center",
    gap: "8px",
    backgroundColor: COLOR.primaryDark,
    color: COLOR.onPrimary,
    border: "none",
    padding: "13px 26px",
    borderRadius: "999px",
    fontSize: "14.5px",
    fontWeight: 700,
    cursor: "pointer",
    boxShadow: "0 8px 20px rgba(0,0,0,0.18)",
  },

  areasCard: {
    margin: "0 16px 28px",
    backgroundColor: COLOR.paper,
    borderRadius: "18px",
    padding: "18px 18px 20px",
    boxShadow: "0 2px 12px rgba(0,0,0,0.06)",
    flex: 1,
    boxSizing: "border-box",
  },
  areasHeader: {
    display: "flex",
    alignItems: "center",
    justifyContent: "space-between",
    marginBottom: "12px",
  },
  areasTitle: { fontSize: "14.5px", fontWeight: 800, color: COLOR.ink, margin: 0 },
  areasCount: {
    fontSize: "11px",
    fontWeight: 700,
    color: COLOR.primaryDark,
    background: COLOR.mist,
    padding: "3px 9px",
    borderRadius: "999px",
  },

  areaSearchWrap: {
    display: "flex",
    alignItems: "center",
    gap: "8px",
    border: `1.5px solid ${COLOR.line}`,
    borderRadius: "10px",
    padding: "9px 12px",
    marginBottom: "14px",
    background: COLOR.mist,
  },
  areaSearchInput: {
    flex: 1,
    minWidth: 0,
    border: "none",
    outline: "none",
    background: "transparent",
    fontSize: "13px",
    color: COLOR.ink,
  },

  areasList: {
    display: "flex",
    flexDirection: "column",
    gap: "8px",
    maxHeight: "320px",
    overflowY: "auto",
  },
  areaSkeleton: { height: "42px", borderRadius: "10px", backgroundColor: COLOR.mist },
  areasEmptyText: { fontSize: "13px", color: COLOR.muted, textAlign: "center", padding: "18px 4px" },

  areaChip: {
    display: "flex",
    alignItems: "center",
    gap: "10px",
    padding: "11px 12px",
    borderRadius: "10px",
    backgroundColor: COLOR.mist,
  },
  areaChipIconWrap: { display: "flex", flexShrink: 0 },
  areaChipName: { flex: 1, fontSize: "13px", fontWeight: 600, color: COLOR.ink, minWidth: 0 },
  areaChipPin: {
    fontSize: "11px",
    fontWeight: 700,
    color: COLOR.primaryDark,
    backgroundColor: COLOR.paper,
    padding: "3px 8px",
    borderRadius: "999px",
    flexShrink: 0,
  },
};